import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Send, Bot, Sparkles, TrendingUp, FileSearch, Target, ChevronDown, Building2 } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useAuth } from '@/lib/supabase';
import { usePortfolioFromSupabase } from '@/lib/supabase/investor-queries';
import { mode } from '@/lib/config/app-mode';
import { useDDChat } from '@/lib/dd-chat/use-dd-chat';
import type { DDStartupContext } from '@/lib/supabase/edge-functions';
import type { DealflowStartup } from '@/lib/state/dealflow-store';

// ============================================================================
// Mock-mode message type (reused from original local chat)
// ============================================================================

interface MockMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// ---------------------------------------------------------------------------
// Module-level message cache — survives tab navigation / component remounts.
// Chat history intentionally does NOT persist across app restarts.
// ---------------------------------------------------------------------------
let _cachedMockMessages: MockMessage[] | null = null;

const QUICK_PROMPTS = [
  { icon: TrendingUp, label: 'Analyze dealflow', prompt: 'Analyze my current dealflow and highlight the top opportunities' },
  { icon: FileSearch, label: 'Due diligence tips', prompt: 'What should I look for when reviewing a startup\'s data room?' },
  { icon: Target, label: 'Portfolio insights', prompt: 'Give me insights on my portfolio performance and diversification' },
];

const INITIAL_MESSAGES: MockMessage[] = [
  {
    id: '1',
    role: 'assistant',
    content: "Hello! I'm your AI Investment Assistant. I can help you analyze startups, review due diligence materials, and provide insights on your portfolio. What would you like to know?",
    timestamp: new Date(),
  },
];

// ============================================================================
// Main Screen Component
// ============================================================================

export default function InvestorAssistantScreen() {
  useTrackScreen('Assistant');
  const ddChatTracked = useRef(false);
  useEffect(() => {
    if (ddChatTracked.current) return;
    ddChatTracked.current = true;
    track('dd_chat_opened', { source: 'assistant' });
  }, []);

  // In mock mode, render the original local keyword-matching chat.
  // In production/demo mode, render the real DD chat backed by useDDChat.
  if (mode.isMock) {
    return <MockAssistant />;
  }

  return <RealAssistant />;
}

// ============================================================================
// Real Assistant — backed by useDDChat hook (production / demo)
// ============================================================================

function RealAssistant() {
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const dealflow = useDealflowStore((s) => s.dealflow);
  const [inputText, setInputText] = useState('');
  const scrollViewRef = useRef<ScrollView>(null);

  // Startup selector state
  const [selectedStartup, setSelectedStartup] = useState<DealflowStartup | null>(null);
  const [showStartupPicker, setShowStartupPicker] = useState(false);

  // Build startup context from selected dealflow item
  const startupContext: DDStartupContext | undefined = useMemo(() => {
    if (!selectedStartup) return undefined;
    const s = selectedStartup.startup;
    return {
      name: s.company.name,
      stage: s.company.stage,
      industry: s.company.industry,
      location: s.company.location,
      website: s.company.website,
      foundedYear: s.company.founded,
      tagline: s.company.tagline,
      description: s.company.description,
    };
  }, [selectedStartup]);

  // DD Chat hook — works with or without startup context (general mode)
  const {
    messages,
    isLoading,
    sendMessage,
    suggestedQuestions,
    setStartupContext: updateContext,
    clearHistory,
  } = useDDChat({
    startupContext,
    startupId: selectedStartup?.startup.id,
    onMessageSent: () => {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    },
  });

  // When startup selection changes, update the hook context
  useEffect(() => {
    if (startupContext) {
      updateContext(startupContext);
    }
  }, [startupContext, updateContext]);

  const handleSend = useCallback(() => {
    if (!inputText.trim()) return;
    track('dd_chat_message_sent', { source: 'assistant' });
    sendMessage(inputText.trim());
    setInputText('');
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [inputText, sendMessage]);

  const handleQuickPrompt = useCallback((prompt: string) => {
    setInputText(prompt);
  }, []);

  const handleSuggestionTap = useCallback((question: string) => {
    track('dd_chat_message_sent', { source: 'assistant_suggestion' });
    sendMessage(question);
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [sendMessage]);

  const handleSelectStartup = useCallback((startup: DealflowStartup) => {
    setSelectedStartup(startup);
    setShowStartupPicker(false);
    clearHistory();
  }, [clearHistory]);

  const handleClearStartup = useCallback(() => {
    setSelectedStartup(null);
    setShowStartupPicker(false);
    clearHistory();
  }, [clearHistory]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 200);
    }
  }, [messages.length]);

  // Determine display messages: show welcome message when empty
  const hasMessages = messages.length > 0;

  return (
    <AppLayout title="AI Assistant">
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
            keyboardVerticalOffset={0}
          >
            {/* Header - Mobile only */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="px-5 pt-4 pb-3 border-b"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderColor: colors.border.light
                }}
              >
                <View className="flex-row items-center gap-3">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary[600] }}
                  >
                    <Sparkles size={20} color="white" />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                      AI Assistant
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">
                      {selectedStartup
                        ? `Analyzing ${selectedStartup.startup.company.name}`
                        : 'Investment insights & analysis'}
                    </Text>
                  </View>
                </View>
              </Animated.View>
            )}

            {/* Startup Selector */}
            <View
              className="px-4 py-2 border-b"
              style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
            >
              <AnimatedPressable
                onPress={() => setShowStartupPicker(!showStartupPicker)}
                className="flex-row items-center justify-between px-3 py-2.5 rounded-xl"
                style={{
                  backgroundColor: colors.surface.secondary,
                  borderWidth: 1,
                  borderColor: selectedStartup ? colors.primary[300] : colors.border.light,
                }}
              >
                <View className="flex-row items-center gap-2 flex-1">
                  <Building2 size={16} color={selectedStartup ? colors.primary[600] : colors.text.tertiary} />
                  <Text
                    style={{ color: selectedStartup ? colors.text.primary : colors.text.tertiary }}
                    className="text-sm"
                    numberOfLines={1}
                  >
                    {selectedStartup
                      ? selectedStartup.startup.company.name
                      : 'Select a startup to analyze (optional)'}
                  </Text>
                </View>
                <ChevronDown size={16} color={colors.text.tertiary} />
              </AnimatedPressable>

              {/* Startup picker dropdown */}
              {showStartupPicker && (
                <Animated.View
                  entering={FadeIn.duration(200)}
                  className="mt-2 rounded-xl border overflow-hidden"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderColor: colors.border.light,
                    maxHeight: 240,
                  }}
                >
                  <ScrollView showsVerticalScrollIndicator={false}>
                    {/* General mode option */}
                    <AnimatedPressable
                      onPress={handleClearStartup}
                      className="flex-row items-center gap-3 px-4 py-3 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Sparkles size={16} color={colors.primary[600]} />
                      <View className="flex-1">
                        <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                          General Assistant
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">
                          Ask general investment questions
                        </Text>
                      </View>
                    </AnimatedPressable>

                    {/* Dealflow startups */}
                    {dealflow.map((item) => (
                      <AnimatedPressable
                        key={item.id}
                        onPress={() => handleSelectStartup(item)}
                        className="flex-row items-center gap-3 px-4 py-3 border-b"
                        style={{
                          borderColor: colors.border.light,
                          backgroundColor: selectedStartup?.id === item.id
                            ? `${colors.primary[600]}08`
                            : undefined,
                        }}
                      >
                        <Building2 size={16} color={colors.text.secondary} />
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                            {item.startup.company.name}
                          </Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs">
                            {item.startup.company.industry} · {item.readinessScore != null ? `Score: ${item.readinessScore}` : 'Not scored'}
                          </Text>
                        </View>
                      </AnimatedPressable>
                    ))}

                    {dealflow.length === 0 && (
                      <View className="px-4 py-3">
                        <Text style={{ color: colors.text.tertiary }} className="text-sm text-center">
                          No startups in dealflow yet
                        </Text>
                      </View>
                    )}
                  </ScrollView>
                </Animated.View>
              )}
            </View>

            {/* Messages */}
            <ScrollView
              ref={scrollViewRef}
              className="flex-1 px-4"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingVertical: 16 }}
              style={{ backgroundColor: colors.surface.secondary }}
            >
              {/* Welcome message when no messages yet */}
              {!hasMessages && (
                <Animated.View entering={FadeIn.duration(300)} className="items-start mb-4">
                  <View
                    className="max-w-[85%] rounded-2xl rounded-bl-sm p-4"
                    style={{
                      backgroundColor: colors.surface.primary,
                      shadowColor: colors.text.primary,
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.05,
                      shadowRadius: 3,
                      elevation: 2,
                    }}
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <Bot size={14} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                        AI Assistant
                      </Text>
                    </View>
                    <Text
                      style={{ color: colors.text.primary }}
                      className="text-sm leading-5"
                    >
                      {selectedStartup
                        ? `Hello! I'm ready to analyze ${selectedStartup.startup.company.name}. Ask me about risks, team strength, market position, traction, or anything else you'd like to evaluate.`
                        : "Hello! I'm your AI Investment Assistant. I can help you analyze startups, review due diligence materials, and provide insights on your portfolio. Select a startup above for deeper analysis, or ask me general questions."}
                    </Text>
                  </View>
                </Animated.View>
              )}

              {/* Real messages from useDDChat */}
              {messages.map((message, index) => (
                <Animated.View
                  key={message.id}
                  entering={FadeIn.duration(300)}
                  className={`mb-4 ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <View
                    className={`max-w-[85%] rounded-2xl p-4 ${
                      message.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'
                    }`}
                    style={{
                      backgroundColor:
                        message.role === 'user' ? colors.primary[600] : colors.surface.primary,
                      shadowColor: colors.text.primary,
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.05,
                      shadowRadius: 3,
                      elevation: 2,
                    }}
                  >
                    {message.role === 'assistant' && (
                      <View className="flex-row items-center gap-2 mb-2">
                        <Bot size={14} color={colors.primary[600]} />
                        <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                          AI Assistant
                        </Text>
                      </View>
                    )}
                    <Text
                      style={{
                        color: message.role === 'user' ? colors.text.inverse : colors.text.primary
                      }}
                      className="text-sm leading-5"
                    >
                      {message.content}
                    </Text>
                  </View>
                </Animated.View>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <Animated.View entering={FadeIn.duration(200)} className="items-start mb-4">
                  <View
                    className="rounded-2xl rounded-bl-sm p-4"
                    style={{
                      backgroundColor: colors.surface.primary,
                      shadowColor: colors.text.primary,
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.05,
                      shadowRadius: 3,
                      elevation: 2,
                    }}
                  >
                    <View className="flex-row items-center gap-2">
                      <Bot size={14} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        Thinking...
                      </Text>
                    </View>
                  </View>
                </Animated.View>
              )}

              {/* Suggested questions from LLM */}
              {!isLoading && suggestedQuestions.length > 0 && hasMessages && (
                <Animated.View entering={FadeIn.duration(300)} className="mb-4">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-2">
                    Suggested follow-ups:
                  </Text>
                  <View className="gap-2">
                    {suggestedQuestions.slice(0, 3).map((question, index) => (
                      <AnimatedPressable
                        key={index}
                        onPress={() => handleSuggestionTap(question)}
                        className="px-3 py-2 rounded-lg border"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderColor: colors.border.light,
                        }}
                      >
                        <Text style={{ color: colors.text.secondary }} className="text-sm">
                          {question}
                        </Text>
                      </AnimatedPressable>
                    ))}
                  </View>
                </Animated.View>
              )}
            </ScrollView>

            {/* Quick Prompts — show when few or no messages */}
            {messages.length <= 2 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-4 pb-2"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 8 }}
                  style={{ flexGrow: 0 }}
                >
                  {QUICK_PROMPTS.map((item, index) => (
                    <AnimatedPressable
                      key={index}
                      onPress={() => handleQuickPrompt(item.prompt)}
                      className="flex-row items-center gap-2 px-4 py-2.5 rounded-full"
                      style={{
                        backgroundColor: colors.primary[50],
                        borderWidth: 1,
                        borderColor: colors.primary[200],
                      }}
                    >
                      <item.icon size={16} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[700] }} className="text-sm font-medium">
                        {item.label}
                      </Text>
                    </AnimatedPressable>
                  ))}
                </ScrollView>
              </Animated.View>
            )}

            {/* Input */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-4 pb-4 pt-2"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="flex-row items-end gap-2 p-2 rounded-2xl"
                style={{
                  backgroundColor: colors.surface.secondary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <TextInput
                  style={{ color: colors.text.primary }}
                  className="flex-1 text-sm px-3 py-2 max-h-24"
                  placeholder={
                    selectedStartup
                      ? `Ask about ${selectedStartup.startup.company.name}...`
                      : 'Ask about deals, due diligence, portfolio...'
                  }
                  placeholderTextColor={colors.text.tertiary}
                  value={inputText}
                  onChangeText={setInputText}
                  multiline
                  onSubmitEditing={handleSend}
                />
                <AnimatedPressable
                  onPress={handleSend}
                  disabled={!inputText.trim() || isLoading}
                  className="w-10 h-10 rounded-xl items-center justify-center"
                  style={{
                    backgroundColor: inputText.trim() && !isLoading ? colors.primary[600] : colors.surface.tertiary,
                  }}
                >
                  <Send
                    size={18}
                    color={inputText.trim() && !isLoading ? 'white' : colors.text.tertiary}
                  />
                </AnimatedPressable>
              </View>
            </Animated.View>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AppLayout>
  );
}

// ============================================================================
// Mock Assistant — original keyword-matching chat (mock mode only)
// ============================================================================

function MockAssistant() {
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // Restore cached messages on remount (survives tab navigation)
  const [messages, _setMessages] = useState<MockMessage[]>(
    () => _cachedMockMessages ?? [...INITIAL_MESSAGES]
  );
  // Track which messages existed at mount time (skip enter animation for them)
  const mountedMessageCount = useRef(messages.length);

  // Wrapper that also writes through to the module-level cache
  const setMessages = useCallback(
    (update: MockMessage[] | ((prev: MockMessage[]) => MockMessage[])) => {
      _setMessages((prev) => {
        const next = typeof update === 'function' ? update(prev) : update;
        _cachedMockMessages = next;
        return next;
      });
    },
    []
  );

  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);

  // Auto-scroll to end on mount when restoring cached messages
  useEffect(() => {
    if (_cachedMockMessages && _cachedMockMessages.length > 1) {
      setTimeout(() => scrollViewRef.current?.scrollToEnd({ animated: false }), 50);
    }
  }, []);

  const dealflow = useDealflowStore((s) => s.dealflow);
  // Reads the real portfolio. This used to read `useDealflowStore.portfolio`,
  // which only demo login ever wrote — so in production the assistant told
  // every investor their portfolio was empty.
  const { user: authUser } = useAuth();
  const { data: portfolio = [] } = usePortfolioFromSupabase(authUser?.id);

  const generateResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes('dealflow') || lowerMessage.includes('opportunities')) {
      const screening = dealflow.filter(d => d.status === 'screening').length;
      const interested = dealflow.filter(d => d.status === 'interested').length;
      // Its own line: a deal blocked on the founder is the one an investor may
      // want to chase, and folding it into `interested` would hide exactly the
      // deals that are stuck.
      const awaitingAccess = dealflow.filter(d => d.status === 'awaiting_access').length;
      // Unscored deals are not 'high scoring' — they are unknown. Excluded.
      const highScores = dealflow.filter(d => d.readinessScore != null && d.readinessScore >= 75);

      return `Based on your current dealflow:\n\n` +
        `\u2022 **${dealflow.length} total startups** in your pipeline\n` +
        `\u2022 ${screening} awaiting screening\n` +
        `\u2022 ${interested} marked as interested\n` +
        `\u2022 ${awaitingAccess} awaiting data-room access from the founder\n\n` +
        `**Top opportunities** (score \u226575):\n` +
        highScores.map(d => `\u2022 ${d.startup.company.name} - ${d.readinessScore}/100 (${d.startup.company.industry})`).join('\n') +
        `\n\nI recommend prioritizing startups with high confidence scores and low critical risks in their Inverio assessment.`;
    }

    if (lowerMessage.includes('due diligence') || lowerMessage.includes('data room')) {
      return `Here are key things to look for in a startup's data room:\n\n` +
        `**Financial Documents:**\n` +
        `\u2022 Verify revenue claims with bank statements\n` +
        `\u2022 Check burn rate and runway calculations\n` +
        `\u2022 Review unit economics (CAC, LTV, margins)\n\n` +
        `**Legal & Cap Table:**\n` +
        `\u2022 Clean cap table with no red flags\n` +
        `\u2022 IP ownership and assignments\n` +
        `\u2022 Any pending litigation\n\n` +
        `**Traction Proof:**\n` +
        `\u2022 Customer contracts and LOIs\n` +
        `\u2022 Usage metrics and retention data\n` +
        `\u2022 Partnership agreements\n\n` +
        `**Red Flags to Watch:**\n` +
        `\u2022 Missing or outdated documents\n` +
        `\u2022 Discrepancies between pitch and financials\n` +
        `\u2022 Low confidence scores on key criteria`;
    }

    if (lowerMessage.includes('portfolio') || lowerMessage.includes('performance')) {
      const totalInvested = portfolio.reduce((sum, p) => sum + p.investmentAmount, 0);
      const totalValue = portfolio.reduce((sum, p) => sum + (p.investmentAmount * (p.returns || 1)), 0);
      const avgReturn = portfolio.length > 0
        ? portfolio.reduce((sum, p) => sum + (p.returns || 1), 0) / portfolio.length
        : 0;

      return `**Portfolio Overview:**\n\n` +
        `\u2022 ${portfolio.length} active investments\n` +
        `\u2022 Total invested: $${(totalInvested / 1000000).toFixed(1)}M\n` +
        `\u2022 Current value: $${(totalValue / 1000000).toFixed(1)}M\n` +
        `\u2022 Average return: ${avgReturn.toFixed(1)}x\n\n` +
        `**Diversification:**\n` +
        `Your portfolio spans ${new Set(portfolio.map(p => p.startup.company.industry)).size} industries.\n\n` +
        `**Recommendation:** Consider your sector concentration and stage distribution when evaluating new deals.`;
    }

    if (lowerMessage.includes('risk') || lowerMessage.includes('score')) {
      return `**Understanding Inverio Ratings:**\n\n` +
        `The Inverio Score has 3 components:\n\n` +
        `1. **Overall Score** (0-100)\n` +
        `   Investment readiness across 15 criteria\n\n` +
        `2. **Confidence Score** (%)\n` +
        `   How many claims are verified with documents\n` +
        `   Higher = more trustworthy data\n\n` +
        `3. **Risk Assessment**\n` +
        `   \u2022 Critical (red): Potential deal breakers\n` +
        `   \u2022 Medium (yellow): Can be mitigated\n` +
        `   \u2022 Low (green): Minor improvements\n\n` +
        `**My advice:** Prioritize startups with high confidence scores (>70%) and no critical risks.`;
    }

    return `I can help you with:\n\n` +
      `\u2022 **Dealflow analysis** - Review your pipeline and top opportunities\n` +
      `\u2022 **Due diligence guidance** - What to look for in data rooms\n` +
      `\u2022 **Portfolio insights** - Performance and diversification analysis\n` +
      `\u2022 **Inverio Score interpretation** - Understanding startup ratings\n\n` +
      `What would you like to explore?`;
  };

  const handleSend = () => {
    if (!inputText.trim()) return;
    track('dd_chat_message_sent', { source: 'assistant' });

    const userMessage: MockMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate AI response delay
    setTimeout(() => {
      const response = generateResponse(userMessage.content);
      const assistantMessage: MockMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1000);

    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const handleQuickPrompt = (prompt: string) => {
    setInputText(prompt);
  };

  return (
    <AppLayout title="AI Assistant">
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
            keyboardVerticalOffset={0}
          >
            {/* Header - Mobile only */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="px-5 pt-4 pb-3 border-b"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderColor: colors.border.light
                }}
              >
                <View className="flex-row items-center gap-3">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary[600] }}
                  >
                    <Sparkles size={20} color="white" />
                  </View>
                  <View>
                    <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                      AI Assistant
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">
                      Investment insights & analysis
                    </Text>
                  </View>
                </View>
              </Animated.View>
            )}

          {/* Messages */}
          <ScrollView
            ref={scrollViewRef}
            className="flex-1 px-4"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingVertical: 16 }}
            style={{ backgroundColor: colors.surface.secondary }}
          >
            {messages.map((message, index) => (
              <Animated.View
                key={message.id}
                entering={index >= mountedMessageCount.current ? FadeIn.duration(300) : undefined}
                className={`mb-4 ${message.role === 'user' ? 'items-end' : 'items-start'}`}
              >
                <View
                  className={`max-w-[85%] rounded-2xl p-4 ${
                    message.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'
                  }`}
                  style={{
                    backgroundColor:
                      message.role === 'user' ? colors.primary[600] : colors.surface.primary,
                    shadowColor: colors.text.primary,
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.05,
                    shadowRadius: 3,
                    elevation: 2,
                  }}
                >
                  {message.role === 'assistant' && (
                    <View className="flex-row items-center gap-2 mb-2">
                      <Bot size={14} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                        AI Assistant
                      </Text>
                    </View>
                  )}
                  <Text
                    style={{
                      color: message.role === 'user' ? colors.text.inverse : colors.text.primary
                    }}
                    className="text-sm leading-5"
                  >
                    {message.content}
                  </Text>
                </View>
              </Animated.View>
            ))}

            {isTyping && (
              <Animated.View entering={FadeIn.duration(200)} className="items-start mb-4">
                <View
                  className="rounded-2xl rounded-bl-sm p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: colors.text.primary,
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.05,
                    shadowRadius: 3,
                    elevation: 2,
                  }}
                >
                  <View className="flex-row items-center gap-2">
                    <Bot size={14} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">
                      Thinking...
                    </Text>
                  </View>
                </View>
              </Animated.View>
            )}
          </ScrollView>

          {/* Quick Prompts */}
          {messages.length <= 2 && (
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-4 pb-2"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8 }}
                style={{ flexGrow: 0 }}
              >
                {QUICK_PROMPTS.map((item, index) => (
                  <AnimatedPressable
                    key={index}
                    onPress={() => handleQuickPrompt(item.prompt)}
                    className="flex-row items-center gap-2 px-4 py-2.5 rounded-full"
                    style={{
                      backgroundColor: colors.primary[50],
                      borderWidth: 1,
                      borderColor: colors.primary[200],
                    }}
                  >
                    <item.icon size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[700] }} className="text-sm font-medium">
                      {item.label}
                    </Text>
                  </AnimatedPressable>
                ))}
              </ScrollView>
            </Animated.View>
          )}

          {/* Input */}
          <Animated.View
            entering={FadeInUp.delay(200).duration(500)}
            className="px-4 pb-4 pt-2"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View
              className="flex-row items-end gap-2 p-2 rounded-2xl"
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <TextInput
                style={{ color: colors.text.primary }}
                className="flex-1 text-sm px-3 py-2 max-h-24"
                placeholder="Ask about deals, due diligence, portfolio..."
                placeholderTextColor={colors.text.tertiary}
                value={inputText}
                onChangeText={setInputText}
                multiline
                onSubmitEditing={handleSend}
              />
              <AnimatedPressable
                onPress={handleSend}
                disabled={!inputText.trim()}
                className="w-10 h-10 rounded-xl items-center justify-center"
                style={{
                  backgroundColor: inputText.trim() ? colors.primary[600] : colors.surface.tertiary,
                }}
              >
                <Send
                  size={18}
                  color={inputText.trim() ? 'white' : colors.text.tertiary}
                />
              </AnimatedPressable>
            </View>
          </Animated.View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
    </AppLayout>
  );
}
